import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
	Lock,
	Database,
	Share2,
	ExternalLink,
	UserCheck,
	RefreshCw,
	Mail,
} from "lucide-react";
import { Link } from "react-router-dom";
import ContactBlock from "../../components/contact/ContactBlock";

const PrivacyPolicy = () => {
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const sections = [
		{
			icon: <Database className="w-8 h-8 text-primary" />,
			title: "What information do we collect?",
			content: [
				{
					subtitle: "Direct Information",
					text: "When you create an account or make a purchase, we collect information you provide directly, such as your name, email, and contact information.",
				},
				{
					subtitle: "Automatic Information",
					text: "We use cookies and web beacons to collect information about your device and how you use our services.",
				},
				{
					subtitle: "Other Sources",
					text: "We may also collect information from public databases and other companies.",
				},
			],
		},
		{
			icon: <Share2 className="w-8 h-8 text-primary" />,
			title: "How do we share your information?",
			content: [
				{
					text: "We'll share your information with third parties in these cases:",
					list: [
						"With our partners and service providers who help us run our business",
						"In the case of reorganization, merger, or other business changes",
						"If required by law or to protect our rights and safety",
					],
				},
			],
		},
		{
			icon: <ExternalLink className="w-8 h-8 text-primary" />,
			title: "Links",
			content: [
				{
					text: "Clicking on links may direct you away from our site. We are not responsible for the privacy practices of other sites, so please read their privacy statements.",
				},
			],
		},
		{
			icon: <UserCheck className="w-8 h-8 text-primary" />,
			title: "Age of Consent",
			content: [
				{
					text: "By using our website, you confirm that you are of the age of majority in your state or province of residence, or that you have given consent for any minor dependents to use this site.",
				},
			],
		},
		{
			icon: <RefreshCw className="w-8 h-8 text-primary" />,
			title: "Changes to This Privacy Policy",
			content: [
				{
					text: "We reserve the right to modify this privacy policy at any time. Please review it frequently, as changes and clarifications take effect immediately upon posting on the website.",
				},
			],
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>Privacy Policy | Off Cut Shades</title>
				<meta
					name="description"
					content="Off Cut Shades privacy policy covering everything you need to know about our policy on the website. Contact our custom window shades support team if any questions."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/help/privacy-policy"
				/>
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<motion.div
						className="flex justify-center mb-6"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ ...bounceTransition, delay: 0.2 }}
					>
						<div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
							<Lock className="w-10 h-10 text-primary" />
						</div>
					</motion.div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						Privacy{" "}
						<span className="relative">
							<span className="text-primary">Policy</span>
						</span>
					</motion.h1>
				</motion.div>

				{/* Policy Sections */}
				<div className="space-y-8">
					{sections.map((section, index) => (
						<motion.div
							key={index}
							className="bg-white rounded-3xl shadow-lg overflow-hidden"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<div className="p-8">
								<div className="flex items-center gap-4 mb-6">
									{section.icon}
									<h2 className="text-2xl font-bold text-gray-900">
										{section.title}
									</h2>
								</div>

								{section.content.map((content, contentIndex) => (
									<div key={contentIndex} className="">
										{content.subtitle && (
											<h3 className="mt-4 text-xl font-semibold text-gray-800">
												{content.subtitle}
											</h3>
										)}
										{content.text && (
											<p className="mb-4 text-gray-600">{content.text}</p>
										)}
										{content.list && (
											<ul className="space-y-2">
												{content.list.map((item, itemIndex) => (
													<li
														key={itemIndex}
														className="flex items-start gap-3"
													>
														<div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
														<span className="text-gray-600">{item}</span>
													</li>
												))}
											</ul>
										)}
									</div>
								))}
							</div>
						</motion.div>
					))}
				</div>
			</main>

			{/* Contact Support Section */}
			<ContactBlock />
		</div>
	);
};

export default PrivacyPolicy;
